import { useState } from 'react';
import toast from 'react-hot-toast';
import { downloadPaymentInvoice } from '~/api';
import Gap from '~/components/Gap';
import Text from '~/components/Text';
import DownloadIcon from '~/icons/DownloadIcon';
import { analytics, ANALYTICS_EVENTS } from '~/services/analyticsService';
import { formatDate } from '~/utils/datetime';
import { useBillingContext } from '../hooks/useBillingContext';
import * as Styles from './styles';

const BillingList = () => {
  const { listIds, listData } = useBillingContext();
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const formatAmount = (amount: string) => {
    const numAmount = parseFloat(amount);
    return `$${numAmount.toFixed(2)}`;
  };

  const formatInvoiceId = (id: number, billingDate: string) => {
    const date = new Date(billingDate);
    const monthYear = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return `Invoice #${id} - ${monthYear}`;
  };

  const formatPlanName = (planName: string) => {
    return planName.charAt(0).toUpperCase() + planName.slice(1).toLowerCase();
  };

  const isPaidStatus = (status: string) => {
    return status.toLowerCase().includes('succeeded') || status.toLowerCase().includes('paid');
  };

  const handleDownloadInvoice = async (paymentId: string, itemId: string) => {
    analytics.capture(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      screen_name: 'Plans',
      button_name: 'Download Invoice',
      button_id: 'billing_invoice_download_cta',
    });

    setDownloadingIds(prev => new Set(prev).add(itemId));

    try {
      const blob = await downloadPaymentInvoice(paymentId);

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error downloading invoice, please contact support team');
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  return (
    <div>
      <ul>
        {listIds.map(billingId => {
          const thisBillingData = listData[billingId];
          const formattedDate = thisBillingData.billing_date
            ? formatDate(thisBillingData.billing_date)
            : null;
          const isDownloading = downloadingIds.has(billingId);
          const canDownload = isPaidStatus(thisBillingData.status);
          const displayStatus = isPaidStatus(thisBillingData.status)
            ? 'Paid'
            : thisBillingData.status;

          return (
            <Styles.ListItem key={billingId}>
              <Styles.LeftContainer>
                <Text size="xsmd" className="bold">
                  {formatInvoiceId(thisBillingData.id, thisBillingData.billing_date)}
                </Text>
                <Gap t={3} />
                <Text size="xs" variant="secondary" className="bold">
                  {formatPlanName(thisBillingData.plan_name)}
                </Text>
                <Gap t={3} />
                <Text size="xxs" variant="neutral">
                  {formattedDate}
                </Text>
              </Styles.LeftContainer>

              <Styles.RightContainer>
                <div className="d-flex">
                  {canDownload && (
                    <Styles.DownloadButton
                      onClick={() => handleDownloadInvoice(thisBillingData.payment_id, billingId)}
                      disabled={isDownloading}
                      isLoading={isDownloading}
                      title="Download Invoice"
                    >
                      {isDownloading ? (
                        <Styles.LoadingSpinner />
                      ) : (
                        <DownloadIcon width={16} height={16} />
                      )}
                    </Styles.DownloadButton>
                  )}
                  <Gap l={8} />
                  <div>
                    <Text className="bold" align="right">
                      {formatAmount(thisBillingData.amount)}
                    </Text>
                    <Gap t={4} />
                    <div className="full-width end-align-center">
                      <Styles.StatusBadge status={thisBillingData.status}>
                        {displayStatus}
                      </Styles.StatusBadge>
                    </div>
                  </div>
                </div>
              </Styles.RightContainer>
            </Styles.ListItem>
          );
        })}
      </ul>
    </div>
  );
};

export default BillingList;
